import React, { FC } from 'react';
import { Alert } from '@clearcover/atlas';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from 'Auth';

import { hasDiscoveredDrivers } from 'QuoteApp/screens/Reports/CurrentCarrierReport';
import { GetConfigResponseType } from 'QuoteApp/services/endpoints/GetConfig';
import { useAppContext } from 'QuoteApp/services/providers';
import styles from './styles.module.css';
import { getCoverageErrors } from 'QuoteApp/services/providers/AppProvider/hooks';
import { isBlank } from 'Shared/utils';

export const getAlertMessage = (
  key: string,
  config?: GetConfigResponseType | null
) => {
  switch (key) {
    case 'softStop':
      return 'This application has received a soft stop.';
    case 'discoveredDrivers':
      return config?.drivers.allowExcludedDrivers
        ? 'We found additional drivers that need to be added, excluded, or removed from this quote.'
        : 'We found additional drivers that need to be added or removed from this quote.';
    case 'discoveredDriversAfterStop':
      return `The drivers on this application are now within the limit${
        config && config.drivers.maxDrivers
          ? ` of ${config.drivers.maxDrivers}`
          : ''
      }. Please review and complete the data for any additional drivers added.`;
    case 'quoteChangeAcknowledgement':
      return 'Product has been toggled. Your coverages might have been changed';
    case 'coverageValidationErrors':
      return 'Policy data is incomplete. Please complete the missing data to continue';
    default:
      return '';
  }
};

export const SummaryAlertBanners: FC = () => {
  const location = useLocation<{ applicationReopened?: boolean }>();
  const { details, config } = useAppContext();
  const { user } = useAuth0();
  const appHasDiscoveredDrivers = hasDiscoveredDrivers(details);

  const displaySoftStopMessage =
    (user?.isCarrierUser || user?.isSystemUser) && details.softStops.length > 0;

  const displayDiscoveredDriversAlert =
    !location.state?.applicationReopened && appHasDiscoveredDrivers;

  const displayDiscoveredDriversAfterStopAlert =
    location.state?.applicationReopened && appHasDiscoveredDrivers;

  const coverageErrors = getCoverageErrors(details.coverages);

  const displayCoverageValidationErrors = !(
    isBlank(coverageErrors.policy) &&
    Object.keys(coverageErrors.vehicles).every((id) =>
      isBlank(coverageErrors.vehicles[id])
    )
  );

  return (
    <div className={styles.container} data-testid="summary-alert-banners">
      {displayCoverageValidationErrors && (
        <Alert
          type="error"
          data-testid="coverage-validation-errors"
          message={getAlertMessage('coverageValidationErrors')}
        />
      )}
      {displaySoftStopMessage && (
        <Alert
          type="info"
          data-testid="soft-stop-alert"
          message={getAlertMessage('softStop')}
        />
      )}
      {displayDiscoveredDriversAlert && (
        <Alert
          type="info"
          data-testid="discovered-drivers-alert"
          message={getAlertMessage('discoveredDrivers', config)}
        />
      )}
      {displayDiscoveredDriversAfterStopAlert && (
        <Alert
          type="info"
          data-testid="discovered-drivers-after-stop-alert"
          message={getAlertMessage('discoveredDriversAfterStop', config)}
        />
      )}
    </div>
  );
};
